import { useState, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import { useSwal } from '@kiper/hooks';
import { apolloErrorHandler } from '@kiper/fns';

import { getWorkstationStatusQuery } from '../../../../../services/auth/actions';
import {
  getLocalKeyPair,
  normalizeWorkstationAuthStatus,
} from '../../../../../services/workstationAuth';

// Cadência fixa de 30s (decisão do ADR: protege o servidor; latência humana aceitável).
const POLL_INTERVAL_MS = 30000;

const isTabHidden = () => typeof document !== 'undefined' && document.hidden;

/**
 * Faz poll do status da solicitação de aprovação enquanto a tela de espera está montada.
 * Carrega o thumbprint local (único por navegador) e consulta o endpoint anônimo de status.
 *
 * Ciclo de vida:
 *   - `pollInterval` dinâmico controla a cadência de forma declarativa: 30s quando
 *     ativo e a aba está visível, 0 (pausado) quando oculta ou em estado terminal;
 *   - `enabled=false` (estado terminal) encerra o poll via `skip`;
 *   - ao voltar o foco, um `refetch` consulta imediatamente (sem esperar 30s);
 *   - um poll que falha é re-tentado no próximo tick (data fica indefinido →
 *     status null → segue aguardando); um toast expõe a exceção ao usuário.
 */
const useWorkstationStatusPolling = ({ enabled }) => {
  const { toast } = useSwal();
  const [publicKeyThumbprint, setPublicKeyThumbprint] = useState(null);
  const [tabVisible, setTabVisible] = useState(!isTabHidden());

  useEffect(() => {
    let active = true;
    getLocalKeyPair().then(keyPair => {
      if (active) setPublicKeyThumbprint(keyPair?.thumbprint || null);
    });
    return () => {
      active = false;
    };
  }, []);

  // `ready` (skip) depende só do thumbprint (sem e-mail na identidade);
  // `active` (cadência) também do enabled. Não amarrar o skip ao enabled: ao entrar
  // em estado terminal o skip continua false, preservando o último `data` (ex.:
  // Approved) — senão o status voltaria a null e o efeito de redirecionamento
  // seria re-executado, cancelando o timer de sucesso.
  const ready = Boolean(publicKeyThumbprint);
  const active = ready && enabled;

  const { data, refetch } = useQuery(getWorkstationStatusQuery, {
    variables: { publicKeyThumbprint },
    skip: !ready,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: error => {
      const formattedErrors = apolloErrorHandler(error);
      if (formattedErrors && formattedErrors.length) {
        toast.fire({ title: formattedErrors.join('\n'), icon: 'error' });
      }
    },
    pollInterval: active && tabVisible ? POLL_INTERVAL_MS : 0,
  });

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const handleVisibilityChange = () => {
      const visible = !isTabHidden();
      setTabVisible(visible);
      // Ao voltar o foco, consulta na hora; a cadência retoma via pollInterval.
      if (visible && active) refetch().catch(() => {});
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    // refetch é lido no momento do evento; reagir só a `active` evita re-registro por render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const status = normalizeWorkstationAuthStatus(
    data?.getWorkstationStatus?.status,
  );

  return { status };
};

export default useWorkstationStatusPolling;
